'use client';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';

import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/shadcn/ui/carousel';

import { PageCareerSchema } from '@/sanity/pages/PageCareerTemplate';

import chevron from '@/public/iq-capital-launch/chevron-right.svg';
import quote from '@/public/global/quote-green.svg';
import DotsBackgroundDark from './DotsBackgroundDark';

/* masked rather than an <img> so each button paints the glyph its own colour */
const CHEVRON_MASK = {
  maskImage: `url(${chevron.src})`,
  WebkitMaskImage: `url(${chevron.src})`,
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
} as const;

export default function TestimonialsSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const items = data?.testimonialsItems;
  const [api, setApi] = useState<CarouselApi>();
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  if (!items?.length) return null;

  return (
    <section
      className={cn(
        'relative overflow-clip bg-[#0A1A1D] pb-[7rem] pt-[9rem] sm:py-[15rem]',
        className,
      )}
    >
      <DotsBackgroundDark className="absolute top-0 left-1/2 -translate-x-1/2" />
      <div className="relative mx-auto flex w-[min(143.25rem,100%)] flex-col items-center px-[3rem] md:px-[5rem] lg:px-0">
        {/* header */}
        <div className="flex w-[min(71rem,100%)] flex-col items-center gap-[1rem] sm:gap-[1.25rem]">
          {data?.testimonialsChip?.text && (
            <Chip
              className="mb-0 [&_p]:uppercase"
              image={data?.testimonialsChip?.icon}
              text={data?.testimonialsChip?.text}
            />
          )}

          {data?.testimonialsTitle && (
            <div className="richtext richtext-center prose-headings:text-white prose-em:font-ivy prose-em:text-[#D9F9B9]">
              <PortableText
                value={data.testimonialsTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* slider — one testimonial per view, arrows live inside the card as drawn */}
        <Carousel
          className="mt-[3.25rem] w-full sm:mt-[6rem]"
          setApi={setApi}
          opts={{ align: 'start' }}
        >
          <CarouselContent>
            {items.map((item, index) => (
              <CarouselItem key={index} className="basis-full pl-0">
                <div className="border-t-[0.375rem] border-[#D9F9B9] bg-[#112628] p-[2.125rem]">
                  <div className="flex flex-col items-stretch gap-[2.5rem] sm:gap-[4rem] lg:flex-row lg:gap-[6.375rem] pb-[1.5rem]">
                    {item?.image?.src && (
                      <div className="relative aspect-[348/320] w-full shrink-0 overflow-clip sm:aspect-[448/462] lg:aspect-auto lg:h-[57.75rem] lg:w-[56rem]">
                        <Image
                          className="absolute left-0 top-0 !h-full !w-full object-cover"
                          src={item.image}
                          alt={item?.name || ''}
                        />
                      </div>
                    )}

                    {/*
                      justify-between rather than the design's literal 136px gap:
                      the card is a fixed height in Figma, so that gap is whatever
                      was left over under a three-line quote. Pinning the footer to
                      the bottom keeps the layout when the copy runs shorter.
                    */}
                    <div className="flex flex-1 flex-col justify-between gap-[7rem] sm:gap-[8rem] lg:w-[76.625rem]">
                      <div className="flex flex-col items-start gap-[1.75rem] sm:gap-[3.5rem] lg:w-[69.625rem]">
                        <Image
                          className="!h-[4rem] !w-[4.25rem]"
                          src={quote}
                          alt=""
                          aria-hidden="true"
                        />

                        {item?.text && (
                          /* the opening sentence is "Strong" — full white against the muted remainder */
                          <div className="richtext richtext-start prose-p:text-[3rem] prose-p:leading-[1.32] prose-p:text-white/60 sm:prose-p:text-[3.25rem] prose-strong:font-normal prose-strong:text-white">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-[3.625rem]">
                        <div className="relative h-[1px] bg-white/10 w-full"></div>

                        <div className="flex items-center justify-between gap-[3rem]">
                          <div className="flex flex-col items-start gap-[1.75rem]">
                            {item?.name && (
                              <p className="mb-0 font-funnel text-[3.25rem] font-medium leading-none text-white">
                                {item.name}
                              </p>
                            )}

                            {item?.position && (
                              <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] text-white/60 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                                {item.position}
                              </p>
                            )}
                          </div>

                          {items.length > 1 && (
                            /* the pill is a stack of rings in the design: base, a lighter ring, then the card colour */
                            <div className="flex shrink-0 gap-[0.75rem] rounded-full bg-[#112628] p-[1.125rem] ring-1 ring-[#293C3E] ring-offset-[0.125rem] ring-offset-[#0A1A1D]">
                              <button
                                type="button"
                                onClick={() => api?.scrollPrev()}
                                disabled={!canPrev}
                                aria-label="Vorherige"
                                className="flex h-[5.125rem] w-[5.125rem] items-center justify-center rounded-full bg-white/10 transition-opacity duration-300 disabled:opacity-40"
                              >
                                <span
                                  className="h-[3rem] w-[3rem] -scale-x-100 bg-white"
                                  style={CHEVRON_MASK}
                                />
                              </button>

                              <button
                                type="button"
                                onClick={() => api?.scrollNext()}
                                disabled={!canNext}
                                aria-label="Nächste"
                                className="flex h-[5.125rem] w-[5.125rem] items-center justify-center rounded-full bg-[#D9F9B9] transition-opacity duration-300 disabled:opacity-40"
                              >
                                <span
                                  className="h-[3rem] w-[3rem] bg-[#0A1A1D]"
                                  style={CHEVRON_MASK}
                                />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  );
}
